import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getCategories, getProducts } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Info, ShoppingBag, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { companyInfo } from '../data/mock';

const CategoryLanding = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [category, setCategory] = useState(null);
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get category data
        const categories = await getCategories();
        const currentCategory = categories.find(c => c.id === id);
        
        if (!currentCategory) {
          navigate('/catalog');
          return;
        }

        setCategory(currentCategory);

        // Get featured product for this category
        const products = await getProducts();
        const categoryProducts = products.filter(p => p.category === id && p.available !== false);
        
        let featured = null;
        if (currentCategory.featuredProductId) {
          featured = categoryProducts.find(p => p.id === currentCategory.featuredProductId);
        }
        
        // If no featured product is set, use the first available product
        if (!featured && categoryProducts.length > 0) {
          featured = categoryProducts[0];
        }

        setFeaturedProduct(featured);
      } catch (error) {
        console.error('Error loading category:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate]);

  const handleInfo = () => {
    if (featuredProduct) {
      navigate(`/product/${featuredProduct.id}`);
    } else {
      navigate(`/catalog?category=${id}`);
    }
  };

  const handleBuy = () => {
    if (featuredProduct) {
      // Create WhatsApp message
      const message = `¡Hola! Me interesa:\n\n*${featuredProduct.name}*\nPrecio: RD$${featuredProduct.price.toLocaleString()}\nFinanciamiento: Desde RD$${featuredProduct.monthly_payment.toLocaleString()}/mes`;
      
      const whatsappUrl = `https://wa.me/${companyInfo.social.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
      
      window.open(whatsappUrl, '_blank');
    } else {
      navigate(`/catalog?category=${id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!category) {
    return null;
  }

  const headline = category.headline?.[language] || category.headline?.es || category.name[language];
  const subtitle = category.subtitle?.[language] || category.subtitle?.es || '';
  const heroImage = category.heroImage || category.image || 'https://images.unsplash.com/photo-1593642632823-8f785ba67e45';

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Apple Style */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={category.name[language]}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {/* Badge */}
            <Badge variant="outline" className="mb-6 bg-white/10 backdrop-blur-sm border-white/20 text-white">
              {category.name[language]}
            </Badge>

            {/* Headline */}
            <h1
              className="text-5xl md:text-7xl lg:text-8xl mb-6 max-w-5xl mx-auto"
              style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                color: '#F5F5F7'
              }}
            >
              {headline}
            </h1>

            {/* Subtitle */}
            {subtitle && (
              <p
                className="text-xl md:text-3xl mb-12 max-w-3xl mx-auto"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.4,
                  color: '#D1D1D6',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                {subtitle}
              </p>
            )}

            {/* Featured Product Info */}
            {featuredProduct && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="mb-12"
              >
                <p className="text-lg text-white/90 mb-2">Producto destacado</p>
                <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">
                  {featuredProduct.name}
                </h2>
                <div className="flex items-center justify-center gap-6">
                  <div>
                    <p className="text-3xl font-bold text-white">
                      RD${featuredProduct.price.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-white/70">Desde</p>
                    <p className="text-xl font-semibold text-white">
                      RD${featuredProduct.monthly_payment.toLocaleString()}/mes
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Button
                size="lg"
                onClick={handleBuy}
                className="px-8 py-6 text-lg bg-white text-black hover:bg-white/90 rounded-full min-w-[200px]"
              >
                <ShoppingBag className="h-5 w-5 mr-2" />
                Comprar
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleInfo}
                className="px-8 py-6 text-lg border-white/30 text-white hover:bg-white/10 backdrop-blur-sm rounded-full min-w-[200px]"
              >
                <Info className="h-5 w-5 mr-2" />
                Información
              </Button>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1, repeat: Infinity, repeatType: "reverse" }}
              className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
            >
              <ArrowRight className="h-6 w-6 text-white/50 rotate-90" />
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Product Grid Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Explora toda la colección</h2>
            <p className="text-xl text-muted-foreground">
              Descubre nuestra selección completa de {category.name[language].toLowerCase()}
            </p>
          </div>
          
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={() => navigate(`/catalog?category=${id}`)}
              className="px-8 py-6 text-lg rounded-full"
            >
              Ver todos los productos
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryLanding;
